import React, { useState, useContext, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import Editor from 'components/elements/Editor'

import { ThemeContext } from 'components/common/App'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

export default function GeneratorVoicePage() {
  const theme = useContext(ThemeContext)

  const [text, setText] = useState()
  const [voice, setVoice] = useState("")
  const [voices, setVoices] = useState([])
  const [mp3, setMp3] = useState(null)
  const [loading, setLoading] = useState(false)
  const [syntTimeout, setSyntTimeout] = useState(300)
  const [elapsed, setElapsed] = useState(0)
  const [requestTime, setRequestTime] = useState(null)
  const startTimeRef = useRef(null)

  useEffect(() => {
    theme.changeTheme("light-bg")
    window.scrollTo(0, 0) // Make sure to be on top of the page

    rpc.post(
      "",
      buildRequest("admin.vocabulary.voices", {})
    ).then(response => {
      if (!response.data.result.message) {
        setVoices(response.data.result.voices)
        setVoice(response.data.result.default)
        if (response.data.result.synt_timeout) {
          setSyntTimeout(response.data.result.synt_timeout)
        }
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        console.error(response.data.result)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loading) return
    const timer = setInterval(() => setElapsed(e => e + 1), 1000)
    return () => clearInterval(timer)
  }, [loading])

  const submitForm = (e) => {
    e.preventDefault()
    setLoading(true)
    setElapsed(0)
    setRequestTime(null)
    startTimeRef.current = performance.now()

    rpc.post(
      "",
      buildRequest(
        "admin.vocabulary.synt",
        {
          text,
          voice,
        },
      )
    ).then(response => {
      if (!response.data.result.message) {
        if (response.data.result.mp3_base64) {
          setMp3(response.data.result.mp3_base64)
        } else {
          console.error("missing mp3_base64 in response")
        }
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        console.error(response.data.result)
      }
    }).catch(err => {
      console.error("Failed to synthesize audio:", err.message)
    }).finally(() => {
      setLoading(false)
      if (startTimeRef.current) {
        const duration = (performance.now() - startTimeRef.current) / 1000
        setRequestTime(duration)
        startTimeRef.current = null
      }
    })
    return false
  }

  const handleChange = (event) => {
    setVoice(event.target.value);
  };

  const progressValue = Math.min((elapsed / syntTimeout) * 100, 100)
  const progressColor = progressValue > 75 ? 'warning' : 'primary'

  return (
    <>
      <Helmet>
        <title>Generator Voice</title>
      </Helmet>
      <Container fixed>
        <form onSubmit={submitForm} method="post">
          <Box sx={{ flexGrow: 1, marginTop: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3">
                  Voice Generator
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Paper>
                  <Editor
                    setValue={setText}
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Stack spacing={2}>
                  <Paper>
                    <Stack padding={2} spacing={1}>
                      <Typography variant="overline" component="p">
                        Voice Parameters
                      </Typography>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={voice}
                        label="Voice"
                        onChange={handleChange}
                      >
                        {voices.map((v) => (
                          <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Paper>
                  <Paper sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="overline" component="p">
                        Generated Audio
                      </Typography>
                      {mp3 ? (
                        <>
                          <Typography variant="body1">
                            {text}
                          </Typography>
                          <audio
                            controls
                            src={`data:audio/mp3;base64,${mp3}`}
                            style={{ width: '100%' }}
                          />
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No audio generated yet
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>
              <Grid item xs={12} sx={{ marginTop: 2 }}>
                <Stack spacing={1}>
                  <Box>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      onClick={submitForm}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Syntethize audio'}
                    </Button>
                    {requestTime !== null && !loading && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 2 }}
                      >
                        Completed in {requestTime.toFixed(1)}s
                      </Typography>
                    )}
                  </Box>
                  {loading && (
                    <Box sx={{ maxWidth: 400 }}>
                      <Typography variant="body2" color="text.secondary">
                        {elapsed}s / {syntTimeout}s
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        color={progressColor}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </form>
      </Container>
    </>
  )
}
